import React, { useState, useRef, useCallback, useMemo } from "react";
import {
  Form,
  Button,
  Modal,
  Collapse,
} from "antd";
import { QuestionCircleOutlined, InfoCircleFilled, UpOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Webcam from "react-webcam";

// Components
import BaseDatePicker from "../../../components/BaseDatePicker/BaseDatePicker";
import Notification from "../../../components/Notification/Notification";
import { RaceRadioGroup } from "../../../components/GlobalComponents/GlobalComponents";
import ViewRegisterPatient from "./components/ViewRegisterPatient";

// Section Components
import BasicInformationSection from "./components/BasicInformationSection";
import ContactInformationSection from "./components/ContactInformationSection";
import EmergencyContactSection from "./components/EmergencyContactSection";
import PhysicalAddressSection from "./components/PhysicalAddressSection";
import DemographicsSection from "./components/DemographicsSection";
import ReferralSection from "./components/ReferralSection";
import IdentificationSection from "./components/IdentificationSection";
import ImageCaptureSection from "./components/ImageCaptureSection";
import PaymentServicesSection from "./components/PaymentServicesSection";

// Contexts and Hooks
import { useStateGlobalContext } from "../../../contexts/GlobalContext";
import { useSearchPatient } from "../../../API/apiUtils";

// Utils and API
import PatientService from "../../../services/patientService";
import { ageCalculator } from "../../../utils/ageCalculator";

// Styles
import "./patientRegistration.css";
const { Panel } = Collapse;

// Constants
const ALL_PANEL_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];


function RegisterPatient() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const webcamRef = useRef(null);
  const { officeSetting } = useStateGlobalContext();
  
  // Consolidated state management
  const [formState, setFormState] = useState({
    data: null,
    visible: false,
    loading: false,
    activeKeys: [],
  });
  
  const [conditionalFields, setConditionalFields] = useState({
    gender: false,
    isSame: false,
    isMinor: false,
    martialStatus: false,
    sexual_Orientation: false,
  });
  
  const [images, setImages] = useState({
    profile: null,
    passport: null,
    cameraOpen: false,
  });
  
  const [dateState, setDateState] = useState({
    dob: null,
    age: null,
  });

  // Memoized values
  const timezone = useMemo(() => officeSetting?.facility_time_zone || 'UTC', [officeSetting]);
  
  const {
    options: firstNameOptions,
    handleSearchPatient: handleSearchFirstName,
  } = useSearchPatient(({ first_name }) => first_name);
  const {
    options: lastNameOptions,
    handleSearchPatient: handleSearchLastName,
  } = useSearchPatient(({ last_name }) => last_name);

  // Memoized functions
  const toggleAllPanels = useCallback(() => {
    setFormState(prev => ({
      ...prev,
      activeKeys: prev.activeKeys.length === 0 ? ALL_PANEL_KEYS : []
    }));
  }, []);

  const disabledDate = useCallback((current) => {
    return current && current > new Date();
  }, []);

  const handleDateChange = useCallback((date) => {
    setDateState({
      dob: date,
      age: date ? ageCalculator(date, timezone) : null
    });
  }, [timezone]);

  const captureImage = useCallback((type) => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setImages(prev => ({
        ...prev,
        [type]: imageSrc,
        cameraOpen: false
      }));
    }
  }, []);

  const clearImages = useCallback((type) => {
    setImages(prev => ({
      ...prev,
      [type]: null
    }));
  }, []);

  const closeModal = useCallback(() => {
    setImages(prev => ({ ...prev, cameraOpen: false }));
  }, []);

  const handleCancel = useCallback(() => {
    setFormState(prev => ({ ...prev, visible: false }));
    setImages({ profile: null, passport: null, cameraOpen: false });
  }, []);

  const handleBackHome = useCallback(() => {
    navigate(`/main/work_dashboard`);
  }, [navigate]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  const onFinish = useCallback(async (values) => {
    setFormState(prev => ({ ...prev, loading: true }));
    
    try {
      const patientData = PatientService.buildPatientData(values, conditionalFields, images);
      const result = await PatientService.registerPatient(patientData);
      
      if (result.success) {
        Notification.success("Patient has been registered Successfully");
        setFormState(prev => ({ 
          ...prev, 
          visible: true, 
          data: result.data, 
          loading: false 
        }));
        form.resetFields();
      } else {
        throw new Error("Registration failed");
      }
    } catch (error) {
      console.error("Registration error:", error);
      Notification.error(error.message || "Registration failed. Please try again.");
      setFormState(prev => ({ ...prev, loading: false }));
    }
  }, [conditionalFields, images, form]);



  const handleSameAsMailing = useCallback((e) => {
    const isSame = e.target.checked;
    setConditionalFields(prev => ({ 
      ...prev, 
      isSame: !isSame 
    }));
    form.setFieldsValue({
      mailingAddress1: "",
      mailingAddress2: "",
      mailingCity: "",
      mailingState: "",
      mailingZip: "",
      mailingCountry: "",
    });
  }, [form]);



  return (
    <div className="mb-4">
      <div className="flex justify-between">
        <h1 className="py-4 text-[1.2rem] font-semibold">New Registration</h1>
        <Button
          type="primary"
          onClick={toggleAllPanels}
          style={{ marginBottom: "16px" }}
        >
          {formState.activeKeys.length === 0 ? "Open All" : "Close All"}
        </Button>
      </div>
      <Form
        layout="vertical"
        name="register-patient"
        form={form}
        onFinish={onFinish}
        scrollToFirstError={true}
      >
        <Collapse
          className="mb-4"
          defaultActiveKey="1"
          collapsible="disabled"
          expandIconPosition="end"
          bordered={false}
        >
          <Panel header="Basic Information" key="1">
            <BasicInformationSection
              conditionalFields={conditionalFields}
              setConditionalFields={setConditionalFields}
              dateState={dateState}
              handleDateChange={handleDateChange}
              disabledDate={disabledDate}
              firstNameOptions={firstNameOptions}
              handleSearchFirstName={handleSearchFirstName}
              lastNameOptions={lastNameOptions}
              handleSearchLastName={handleSearchLastName}
            />
          </Panel>
        </Collapse>

        <Collapse
          className="mb-4"
          activeKey={formState.activeKeys}
          onChange={(keys) => setFormState(prev => ({ ...prev, activeKeys: keys }))}
          expandIconPosition="end"
          bordered={false}
        >
          <Panel header="Contact Information" key="2">
            <ContactInformationSection />
          </Panel>
        </Collapse>

        <Collapse
          className="mb-4"
          expandIconPosition="end"
          activeKey={formState.activeKeys}
          onChange={(keys) => setFormState(prev => ({ ...prev, activeKeys: keys }))}
          bordered={false}
        >
          <Panel header="Emergency Contact" key="3">
            <EmergencyContactSection />
          </Panel>
        </Collapse>

        <Collapse
          className="mb-4"
          expandIconPosition="end"
          activeKey={formState.activeKeys}
          onChange={(keys) => setFormState(prev => ({ ...prev, activeKeys: keys }))}
          bordered={false}
        >
          <Panel header="Physical Address" key="4">
            <PhysicalAddressSection
              conditionalFields={conditionalFields}
              handleSameAsMailing={handleSameAsMailing}
            />
          </Panel>
        </Collapse>

        <Collapse
          className="mb-4"
          expandIconPosition="end"
          activeKey={formState.activeKeys}
          onChange={(keys) => setFormState(prev => ({ ...prev, activeKeys: keys }))}
          bordered={false}
        >
          <Panel header="Demographics" key="5">
            <DemographicsSection />
          </Panel>
        </Collapse>

        <Collapse
          className="mb-4"
          expandIconPosition="end"
          activeKey={formState.activeKeys}
          onChange={(keys) => setFormState(prev => ({ ...prev, activeKeys: keys }))}
          bordered={false}
        >
          <Panel header="Referral From" key="6">
            <ReferralSection />
          </Panel>
        </Collapse>

        <Collapse
          className="mb-4"
          expandIconPosition="end"
          activeKey={formState.activeKeys}
          onChange={(keys) => setFormState(prev => ({ ...prev, activeKeys: keys }))}
          bordered={false}
        >
          <Panel header="Identification Cards" key="7">
            <IdentificationSection />
          </Panel>
        </Collapse>

        <Collapse
          className="mb-4"
          expandIconPosition="end"
          activeKey={formState.activeKeys}
          onChange={(keys) => setFormState(prev => ({ ...prev, activeKeys: keys }))}
          bordered={false}
        >
          <Panel header="Capture Profile and ID Picture" key="8">
            <ImageCaptureSection
              images={images}
              setImages={setImages}
              clearImages={clearImages}
            />

          </Panel>
        </Collapse>

        <Collapse
          className="mb-4"
          expandIconPosition="end"
          activeKey={formState.activeKeys}
          onChange={(keys) => setFormState(prev => ({ ...prev, activeKeys: keys }))}
          bordered={false}
        >
          <Panel header="Payment Services" key="9">
            <PaymentServicesSection />
          </Panel>
        </Collapse>
        <div className="flex justify-between items-center mt-4">
          <div className="flex">
            <Button onClick={handleBackHome}>Cancel</Button>
            <Button
              type={formState.loading ? "default" : "primary"}
              htmlType="submit"
              className="ml-2"
              disabled={formState.loading}
              loading={formState.loading}
            >
              Register Patient
            </Button>
          </div>
          <Button
            type="default"
            icon={<UpOutlined />}
            onClick={scrollToTop}
            className="back-to-top-btn"
            title="Back to Top"
          >
            Back to Top
          </Button>
        </div>
      </Form>

      {formState.visible && (
        <ViewRegisterPatient
          open={formState.visible}
          onCancel={handleCancel}
          data={formState.data}
        />
      )}
      <Modal
        open={images.cameraOpen}
        title={`Capture Image`}
        onCancel={closeModal}
        footer={null}
        closeIcon={
          <Button size="small" className="app-close-icon" shape="round">
            Close
          </Button>
        }
      >
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          style={{ width: "100%" }}
        />
        <div className="flex justify-between mt-2">
          <Button
            key="submit-profile"
            type="primary"
            onClick={() => captureImage("profile")}
          >
            Take Profile Picture
          </Button>
          <Button
            className="ml-2"
            key="submit-passport"
            type="primary"
            onClick={() => captureImage("passport")}
          >
            Take ID Card Picture
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default React.memo(RegisterPatient);
